import { useState } from "react";
import { useApp } from "@/lib/AppContext";
import { t } from "@/lib/translations";

const defaultOffer = {
  title: "", title_en: "", description: "", description_en: "",
  offer_type: "discount_percent", discount_value: 0, max_discount_amount: 0,
  terms: "", terms_en: "", weight: 10, daily_win_limit: 0, monthly_win_limit: 0, total_win_limit: 0,
  min_invoice_amount: 0, validity_days: 30, start_date: "", end_date: "", is_active: true
};

export default function OfferModal({ offer, onSave, onClose }) {
  const [form, setForm] = useState(offer || defaultOffer);
  const [saving, setSaving] = useState(false);
  const { lang } = useApp();
  const tr = t[lang];

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.title.trim()) { alert(lang === "ar" ? "يرجى إدخال اسم العرض" : "Please enter the offer name"); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const offerTypeOptions = [
    { value: "discount_percent", ar: "خصم نسبة (%)", en: "Percentage Discount (%)" },
    { value: "discount_amount", ar: "خصم مبلغ (ر.س)", en: "Fixed Discount (SAR)" },
    { value: "free_service", ar: "خدمة مجانية", en: "Free Service" },
    { value: "free_delivery", ar: "توصيل مجاني", en: "Free Delivery" },
    { value: "no_luck", ar: "حظ أوفر", en: "No Luck" },
  ];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-border">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-black text-foreground">
            {offer ? `✏️ ${tr.editOffer}` : `➕ ${tr.addOffer}`}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Arabic & English Names */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">
                اسم العرض (عربي) *
              </label>
              <input
                type="text"
                value={form.title}
                onChange={e => set("title", e.target.value)}
                placeholder="مثال: خصم 20% على الفاتورة"
                dir="rtl"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">
                Offer Name (English)
              </label>
              <input
                type="text"
                value={form.title_en || ""}
                onChange={e => set("title_en", e.target.value)}
                placeholder="e.g. 20% Invoice Discount"
                dir="ltr"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
              />
            </div>
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">وصف العرض (عربي)</label>
              <textarea
                value={form.description}
                onChange={e => set("description", e.target.value)}
                rows={2}
                placeholder="وصف مختصر للعرض..."
                dir="rtl"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors resize-none bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Description (English)</label>
              <textarea
                value={form.description_en || ""}
                onChange={e => set("description_en", e.target.value)}
                rows={2}
                placeholder="Brief offer description..."
                dir="ltr"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors resize-none bg-background"
              />
            </div>
          </div>

          {/* Type & Value */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">{tr.offerType}</label>
              <select
                value={form.offer_type}
                onChange={e => set("offer_type", e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
              >
                {offerTypeOptions.map(o => (
                  <option key={o.value} value={o.value}>
                    {lang === "ar" ? o.ar : o.en}
                  </option>
                ))}
              </select>
            </div>
            {["discount_percent", "discount_amount"].includes(form.offer_type) && (
              <div>
                <label className="block text-sm font-bold mb-1">
                  {lang === "ar"
                    ? `قيمة الخصم ${form.offer_type === "discount_percent" ? "(%)" : "(ر.س)"}`
                    : `Discount Value ${form.offer_type === "discount_percent" ? "(%)" : "(SAR)"}`}
                </label>
                <input
                  type="number"
                  value={form.discount_value}
                  onChange={e => set("discount_value", parseFloat(e.target.value) || 0)}
                  min={0}
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
                />
              </div>
            )}
            {form.offer_type === "discount_percent" && (
              <div>
                <label className="block text-sm font-bold mb-1">
                  {lang === "ar" ? "الحد الأقصى للخصم (ر.س) — 0 = بلا حد" : "Max Discount Amount (SAR) — 0 = unlimited"}
                </label>
                <input
                  type="number"
                  value={form.max_discount_amount || 0}
                  onChange={e => set("max_discount_amount", parseFloat(e.target.value) || 0)}
                  min={0}
                  placeholder="مثال: 20"
                  className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {lang === "ar" ? "مثال: خصم 15% بحد أقصى 20 ر.س" : "e.g. 15% off, capped at SAR 20"}
                </p>
              </div>
            )}
          </div>

          {/* Terms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">شروط العرض (عربي)</label>
              <textarea
                value={form.terms}
                onChange={e => set("terms", e.target.value)}
                rows={2}
                placeholder="مثال: صالح للفاتورة الأقل من 200 ريال..."
                dir="rtl"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors resize-none bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Terms (English)</label>
              <textarea
                value={form.terms_en || ""}
                onChange={e => set("terms_en", e.target.value)}
                rows={2}
                placeholder="e.g. Valid for invoices under 200 SAR..."
                dir="ltr"
                className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors resize-none bg-background"
              />
            </div>
          </div>

          {/* Weight & Limits */}
          <div className="bg-secondary/30 rounded-xl p-4 space-y-4">
            <h3 className="font-bold text-sm text-foreground">⚖️ {lang === "ar" ? "الأوزان والقيود" : "Weights & Limits"}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold mb-1">{tr.weight}</label>
                <input type="number" value={form.weight} onChange={e => set("weight", parseInt(e.target.value) || 0)} min={1} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
                <p className="text-xs text-muted-foreground mt-1">{lang === "ar" ? "كلما كان أعلى، زادت نسبة الفوز" : "Higher = more likely to win"}</p>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">{lang === "ar" ? "الحد اليومي (0=بلا حد)" : "Daily Limit (0=unlimited)"}</label>
                <input type="number" value={form.daily_win_limit} onChange={e => set("daily_win_limit", parseInt(e.target.value) || 0)} min={0} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">{lang === "ar" ? "الحد الشهري (0=بلا حد)" : "Monthly Limit (0=unlimited)"}</label>
                <input type="number" value={form.monthly_win_limit || 0} onChange={e => set("monthly_win_limit", parseInt(e.target.value) || 0)} min={0} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">{lang === "ar" ? "الحد الكلي للحملة (0=بلا حد)" : "Campaign Total Limit (0=unlimited)"}</label>
                <input type="number" value={form.total_win_limit} onChange={e => set("total_win_limit", parseInt(e.target.value) || 0)} min={0} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">{lang === "ar" ? "الحد الأدنى للفاتورة (ر.س)" : "Min. Invoice (SAR)"}</label>
                <input type="number" value={form.min_invoice_amount} onChange={e => set("min_invoice_amount", parseFloat(e.target.value) || 0)} min={0} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">{lang === "ar" ? "مدة صلاحية الكوبون (أيام)" : "Coupon Validity (days)"}</label>
                <input type="number" value={form.validity_days} onChange={e => set("validity_days", parseInt(e.target.value) || 30)} min={1} className="w-full px-3 py-2 border-2 border-border rounded-lg focus:outline-none focus:border-primary text-sm bg-background" />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold mb-1">{lang === "ar" ? "تاريخ البداية" : "Start Date"}</label>
              <input type="date" value={form.start_date || ""} onChange={e => set("start_date", e.target.value)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background" />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">{lang === "ar" ? "تاريخ النهاية" : "End Date"}</label>
              <input type="date" value={form.end_date || ""} onChange={e => set("end_date", e.target.value)} className="w-full px-4 py-2.5 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-colors bg-background" />
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => set("is_active", !form.is_active)}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.is_active ? "bg-green-500" : "bg-gray-400"}`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-1" : lang === "ar" ? "right-1" : "left-1"}`}></span>
            </button>
            <label className="text-sm font-medium">
              {form.is_active
                ? `✅ ${lang === "ar" ? "العرض مفعّل" : "Offer Active"}`
                : `❌ ${lang === "ar" ? "العرض موقوف" : "Offer Inactive"}`}
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border-2 border-border rounded-xl font-medium hover:bg-secondary transition-colors text-foreground">
            {tr.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-primary text-primary-foreground font-bold rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-60"
          >
            {saving ? tr.saving : offer ? `💾 ${tr.save}` : `➕ ${tr.addOffer}`}
          </button>
        </div>
      </div>
    </div>
  );
}